import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useSEO } from "../../lib/seo";
import { toast } from "sonner";
import { Clock, MapPin, CurrencyDollar, Globe, User, ArrowRight, CheckCircle, Warning, Star } from "@phosphor-icons/react";

const STATUS_COLOR = {
  open: "bg-slate-100 text-slate-700",
  interviewing: "bg-blue-100 text-blue-700",
  awarded: "bg-amber-100 text-amber-700",
  in_progress: "bg-blue-100 text-blue-700",
  completed: "bg-purple-100 text-purple-700",
  released: "bg-green-100 text-green-700",
  disputed: "bg-red-100 text-red-700",
  refunded: "bg-slate-100 text-slate-700",
};

function ReviewForm({ jobId, onSubmitted }) {
  const [r, setR] = useState({ communication: 5, quality: 5, timeliness: 5, professionalism: 5, comment: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/virtual/reviews", { job_id: jobId, ...r });
      toast.success("Review submitted");
      onSubmitted();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit review");
    } finally {
      setSaving(false);
    }
  };
  const stars = (k) => (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button type="button" key={n} onClick={() => setR((s) => ({ ...s, [k]: n }))} data-testid={`review-${k}-${n}`}>
          <Star size={20} weight={n <= r[k] ? "fill" : "regular"} className={n <= r[k] ? "text-accent-red" : "text-slate-300"} />
        </button>
      ))}
    </div>
  );
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="review-form">
      {["communication", "quality", "timeliness", "professionalism"].map((k) => (
        <div key={k} className="flex items-center justify-between">
          <span className="text-sm capitalize text-slate-700">{k}</span>
          {stars(k)}
        </div>
      ))}
      <textarea value={r.comment} onChange={(e) => setR({ ...r, comment: e.target.value })} rows={3} placeholder="Anything you want to add?" className="w-full px-3 py-2 border border-slate-300 text-sm" data-testid="review-comment" />
      <button type="submit" disabled={saving} className="w-full bg-brand text-white px-4 py-3 text-sm font-semibold hover:bg-[#122148] disabled:opacity-50" data-testid="review-submit">{saving ? "Submitting…" : "Submit review"}</button>
    </form>
  );
}

export default function VirtualJobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [applying, setApplying] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [appForm, setAppForm] = useState({ cover_letter: "", proposed_budget: "", proposed_timeline: "" });
  const [funding, setFunding] = useState(false);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);

  useSEO(job ? { title: job.title, description: job.description?.slice(0, 155), path: `/virtual/jobs/${id}` } : { title: "Loading…" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/virtual/jobs/${id}`);
      setJob(data);
    } catch {
      setJob(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/virtual/payments-status").then((r) => setPaymentsEnabled(r.data.enabled)).catch(() => {});
    api.get(`/virtual/reviews/job/${id}`).then((r) => setReviews(r.data)).catch(() => {});
  }, [id]);

  const isOwner = user && job && job.client_id === user.user_id;
  const isAwardedFreelancer = user && job && job.awarded_to === user.user_id;
  const myReview = reviews.find((r) => r.reviewer_id === user?.user_id);

  // Handle Stripe return: ?funded=1&session_id=…
  useEffect(() => {
    if (searchParams.get("funded") === "1") {
      const sid = searchParams.get("session_id");
      const poll = async (attempts = 0) => {
        if (attempts >= 8) return;
        try {
          const { data } = await api.get(`/virtual/jobs/${id}/funding-status`, { params: sid ? { session_id: sid } : {} });
          if (data.status === "paid") {
            toast.success("Project funded! Work is in progress.");
            load();
            // Strip query params
            searchParams.delete("funded"); searchParams.delete("session_id");
            setSearchParams(searchParams, { replace: true });
          } else {
            setTimeout(() => poll(attempts + 1), 2000);
          }
        } catch (err) {
          console.warn("Funding poll error:", err);
        }
      };
      poll();
    }
    if (searchParams.get("cancelled") === "1") {
      toast.error("Payment cancelled. The freelancer was not paid.");
    }
  }, [id, searchParams, setSearchParams, load]);

  useEffect(() => {
    if (isOwner) {
      api.get(`/virtual/jobs/${id}/applications`).then((r) => setApplications(r.data)).catch(() => {});
    } else if (user && job) {
      api.get("/virtual/my/applications").then((r) => {
        setHasApplied(r.data.some((a) => a.job_id === id));
      }).catch(() => {});
    }
  }, [id, isOwner, user, job]);

  const submitApp = async (e) => {
    e.preventDefault();
    if (!user) { navigate(`/login?redirect=/virtual/jobs/${id}`); return; }
    if (!appForm.cover_letter || !appForm.proposed_budget || !appForm.proposed_timeline) {
      toast.error("Fill all fields");
      return;
    }
    setApplying(true);
    try {
      await api.post(`/virtual/jobs/${id}/apply`, {
        cover_letter: appForm.cover_letter,
        proposed_budget: parseFloat(appForm.proposed_budget),
        proposed_timeline: appForm.proposed_timeline,
      });
      toast.success("Application sent!");
      setHasApplied(true);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not apply");
    } finally {
      setApplying(false);
    }
  };

  const actionApp = async (application_id, action) => {
    try {
      const { data } = await api.post(`/virtual/applications/${application_id}/action`, { action });
      toast.success(`Application ${data.status}`);
      if (data.conversation_id) {
        navigate(`/chat/${data.conversation_id}`);
        return;
      }
      // refresh
      const { data: apps } = await api.get(`/virtual/jobs/${id}/applications`);
      setApplications(apps);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  const fundApplication = async (application_id) => {
    if (!paymentsEnabled) {
      toast.error("Payments are not yet enabled. We're rolling out Stripe escrow soon.");
      return;
    }
    setFunding(true);
    try {
      const { data } = await api.post(`/virtual/jobs/${id}/fund`, {
        application_id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start funding");
      setFunding(false);
    }
  };

  const markComplete = async () => {
    if (!window.confirm("Mark this project as complete? The client will be notified to approve and release payment.")) return;
    try {
      await api.post(`/virtual/jobs/${id}/mark-complete`);
      toast.success("Marked complete. Waiting for client to approve.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not mark complete");
    }
  };

  const approveAndRelease = async () => {
    if (!window.confirm("Approve work and release payment to the freelancer? This action cannot be undone.")) return;
    try {
      const { data } = await api.post(`/virtual/jobs/${id}/approve`);
      toast.success(`Payment released: $${data.amount_released_cad} CAD`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approve failed");
    }
  };

  const raiseDispute = async () => {
    const ok = window.confirm("Open a dispute? An admin will review and reach out to both sides.");
    if (!ok) return;
    try {
      await api.post(`/virtual/jobs/${id}/dispute`);
      toast.success("Dispute opened. Admin will follow up.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not raise dispute");
    }
  };

  const reloadReviews = async () => {
    const { data } = await api.get(`/virtual/reviews/job/${id}`);
    setReviews(data);
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (!job) return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="pt-24 pb-16 max-w-2xl mx-auto px-4 text-center">
        <h1 className="font-display text-4xl mb-3">Project not found</h1>
        <Link to="/virtual/jobs" className="text-brand font-semibold">← Browse projects</Link>
      </div>
      <Footer />
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <article className="pt-24 pb-16 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/virtual/jobs" className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 hover:text-slate-900">← Browse projects</Link>

        <div className="grid md:grid-cols-3 gap-8 mt-4">
          <div className="md:col-span-2">
            <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-wider font-semibold mb-3">
              <span className="text-accent-red">{job.service_type}</span>
              <span className="text-slate-400">•</span>
              <span className="text-slate-500">{job.pricing_type === "fixed" ? "Fixed price" : "Hourly"}</span>
              <span className={`px-2 py-0.5 ${STATUS_COLOR[job.status] || "bg-slate-100 text-slate-700"}`} data-testid="job-status-pill">{job.status.replace("_", " ")}</span>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl mb-4">{job.title}</h1>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-600 mb-6">
              <span className="flex items-center gap-1"><CurrencyDollar size={14} weight="bold" /> ${job.budget_min}–${job.budget_max} CAD</span>
              {job.deadline && <span className="flex items-center gap-1"><Clock size={14} weight="bold" /> Due {job.deadline}</span>}
              {job.preferred_country && <span className="flex items-center gap-1"><MapPin size={14} weight="bold" /> {job.preferred_country}</span>}
              {job.preferred_language && <span className="flex items-center gap-1"><Globe size={14} weight="bold" /> {job.preferred_language}</span>}
              <span className="flex items-center gap-1"><User size={14} weight="bold" /> Posted by {job.client_name}</span>
            </div>

            <div className="prose prose-slate max-w-none">
              <h3 className="font-display text-lg">Description</h3>
              <p className="whitespace-pre-line text-base text-slate-700">{job.description}</p>
              {job.deliverables && <>
                <h3 className="font-display text-lg mt-6">Deliverables</h3>
                <p className="whitespace-pre-line text-base text-slate-700">{job.deliverables}</p>
              </>}
              {job.skills?.length > 0 && <>
                <h3 className="font-display text-lg mt-6">Skills required</h3>
                <div className="not-prose flex flex-wrap gap-2">
                  {job.skills.map((s) => <span key={s} className="text-xs px-2 py-1 bg-slate-100 border border-slate-200">{s}</span>)}
                </div>
              </>}
            </div>
          </div>

          {/* RIGHT RAIL */}
          <aside className="md:col-span-1">
            {/* Project lifecycle panel — visible when funded/in_progress/completed/released/disputed */}
            {(isOwner || isAwardedFreelancer) && ["awarded", "in_progress", "completed", "released", "disputed", "refunded"].includes(job.status) && (
              <div className="border border-slate-200 p-5 mb-3" data-testid="lifecycle-panel">
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">Project status</div>
                <div className={`inline-block text-xs uppercase tracking-wider font-semibold px-2 py-1 mb-3 ${STATUS_COLOR[job.status]}`}>{job.status.replace("_", " ")}</div>

                {isOwner && job.status === "in_progress" && (
                  <p className="text-xs text-slate-600 mb-3">Funds are in escrow. The freelancer will mark complete when work is done.</p>
                )}
                {isAwardedFreelancer && job.status === "in_progress" && (
                  <>
                    <p className="text-xs text-slate-600 mb-3">Funds are secured. When you finish the work, mark complete to request release.</p>
                    <button onClick={markComplete} className="w-full bg-brand text-white px-4 py-3 text-sm font-semibold hover:bg-[#122148] mb-2" data-testid="mark-complete-btn">
                      Mark project complete
                    </button>
                  </>
                )}
                {isOwner && job.status === "completed" && (
                  <>
                    <p className="text-xs text-slate-600 mb-3">Freelancer has marked complete. Review their work and approve to release payment.</p>
                    <button onClick={approveAndRelease} className="w-full bg-brand text-white px-4 py-3 text-sm font-semibold hover:bg-[#122148] mb-2 inline-flex items-center justify-center gap-2" data-testid="approve-release-btn">
                      <CheckCircle size={14} weight="bold" /> Approve &amp; release payment
                    </button>
                    <button onClick={raiseDispute} className="w-full border border-red-300 text-red-600 px-4 py-3 text-sm font-semibold hover:bg-red-50" data-testid="dispute-btn">
                      Raise a dispute
                    </button>
                  </>
                )}
                {isAwardedFreelancer && job.status === "completed" && (
                  <p className="text-xs text-slate-600">Waiting for client to approve and release payment.</p>
                )}
                {job.status === "released" && (
                  <>
                    <p className="text-xs text-green-700 mb-3 font-semibold">✓ Payment released to freelancer.</p>
                    {!myReview && (
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">Leave a review</div>
                        <ReviewForm jobId={id} onSubmitted={reloadReviews} />
                      </div>
                    )}
                    {myReview && <p className="text-xs text-slate-500 mt-3">✓ You&apos;ve already reviewed this project.</p>}
                  </>
                )}
                {job.status === "disputed" && (
                  <p className="text-xs text-red-700 font-semibold">Dispute open — admin reviewing.</p>
                )}
              </div>
            )}

            {isOwner && job.status === "open" ? (
              <div className="border border-slate-200 p-5">
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Your project</div>
                <div className="text-2xl font-display mb-3">{applications.length} application{applications.length === 1 ? "" : "s"}</div>
                <p className="text-xs text-slate-500">Scroll down to review applications and award + fund your chosen freelancer.</p>
              </div>
            ) : !isOwner && job.status === "open" && hasApplied ? (
              <div className="border border-brand p-5 bg-brand/5">
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-brand mb-2">Application submitted</div>
                <p className="text-sm text-slate-700">The client will review your application and message you if interested.</p>
              </div>
            ) : !isOwner && job.status === "open" && (
              <div className="border border-slate-200 p-5">
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">Apply to this project</div>
                <form onSubmit={submitApp} className="space-y-3" data-testid="apply-form">
                  <textarea value={appForm.cover_letter} onChange={(e) => setAppForm({ ...appForm, cover_letter: e.target.value })} required rows={5} placeholder="Why are you the right freelancer for this project?" className="w-full px-3 py-2 border border-slate-300 text-sm" data-testid="apply-cover" />
                  <input type="number" value={appForm.proposed_budget} onChange={(e) => setAppForm({ ...appForm, proposed_budget: e.target.value })} required placeholder="Your bid (CAD)" className="w-full px-3 py-2 border border-slate-300 text-sm" data-testid="apply-budget" />
                  <input value={appForm.proposed_timeline} onChange={(e) => setAppForm({ ...appForm, proposed_timeline: e.target.value })} required placeholder="Timeline e.g. 10 days" className="w-full px-3 py-2 border border-slate-300 text-sm" data-testid="apply-timeline" />
                  <button type="submit" disabled={applying} className="w-full bg-brand text-white px-4 py-3 text-sm font-semibold hover:bg-[#122148] disabled:opacity-50" data-testid="apply-submit">
                    {applying ? "Sending…" : "Apply now"}
                  </button>
                </form>
              </div>
            )}
          </aside>
        </div>

        {/* APPLICATIONS LIST (owner only) */}
        {isOwner && (
          <section className="mt-16">
            <h2 className="font-display text-2xl mb-6">Applications</h2>
            {applications.length === 0 ? (
              <div className="border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">No applications yet. Share this project to get applicants faster.</div>
            ) : (
              <div className="space-y-3">
                {applications.map((a) => (
                  <div key={a.application_id} className="border border-slate-200 p-5" data-testid={`vapp-${a.application_id}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          {a.candidate_picture ? <img src={a.candidate_picture} alt={a.candidate_name} className="w-9 h-9 rounded-full" /> : <div className="w-9 h-9 bg-slate-200 rounded-full" />}
                          <div>
                            <div className="font-semibold">{a.candidate_name}</div>
                            <div className="text-xs text-slate-500">{a.candidate_headline || "No headline"} • {a.candidate_country || "—"}</div>
                          </div>
                          <span className={`ml-auto text-[10px] uppercase tracking-wider font-semibold px-2 py-1 ${a.status === "accepted" ? "bg-green-100 text-green-700" : a.status === "rejected" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}`}>{a.status}</span>
                        </div>
                        <p className="text-sm text-slate-700 whitespace-pre-line mt-2">{a.cover_letter}</p>
                        <div className="text-xs text-slate-500 mt-2">Bid: <b>${a.proposed_budget}</b> CAD • Timeline: <b>{a.proposed_timeline}</b></div>
                      </div>
                    </div>
                    {a.status === "pending" && job.status === "open" && (
                      <div className="mt-4 flex flex-wrap items-center gap-2">
                        {paymentsEnabled ? (
                          <button onClick={() => fundApplication(a.application_id)} disabled={funding} className="bg-brand text-white text-xs font-semibold px-3 py-2 hover:bg-[#122148] disabled:opacity-50 inline-flex items-center" data-testid={`award-fund-${a.application_id}`}>
                            <CurrencyDollar size={12} weight="bold" className="mr-1" /> Award & fund — ${a.proposed_budget}
                          </button>
                        ) : (
                          <button onClick={() => actionApp(a.application_id, "accept")} className="bg-brand text-white text-xs font-semibold px-3 py-2 hover:bg-[#122148]" data-testid={`accept-${a.application_id}`}>
                            Accept & message <ArrowRight size={12} weight="bold" className="inline ml-1" />
                          </button>
                        )}
                        <button onClick={() => actionApp(a.application_id, "shortlist")} className="border border-slate-300 text-xs font-semibold px-3 py-2 hover:bg-slate-50" data-testid={`shortlist-${a.application_id}`}>Shortlist</button>
                        <button onClick={() => actionApp(a.application_id, "reject")} className="border border-slate-300 text-xs font-semibold px-3 py-2 hover:bg-slate-50 text-red-600" data-testid={`reject-${a.application_id}`}>Reject</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* PAYMENTS DISABLED NOTICE */}
        {isOwner && job.status === "open" && !paymentsEnabled && (
          <div className="mt-6 border-l-4 border-amber-400 bg-amber-50 p-4 flex gap-3" data-testid="payments-disabled-notice">
            <Warning size={20} weight="bold" className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <b>Stripe escrow is being finalized.</b> Until then, accept &amp; message a freelancer to coordinate payment off-platform (e-transfer, PayPal, Wise). All other features (chat, status tracking, reviews) work normally.
            </div>
          </div>
        )}

        {/* REVIEWS DISPLAY */}
        {reviews.length > 0 && (
          <section className="mt-12 border-t border-slate-200 pt-8">
            <h2 className="font-display text-2xl mb-4">Reviews</h2>
            <div className="space-y-3">
              {reviews.map((rv) => (
                <div key={rv.review_id} className="border border-slate-200 p-4" data-testid={`vrev-${rv.review_id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="font-semibold text-sm">{rv.reviewer_name}</div>
                    <span className="text-[10px] uppercase tracking-wider text-slate-500">→ {rv.role === "client_to_freelancer" ? "freelancer" : "client"}</span>
                    <div className="ml-auto flex items-center gap-1 text-accent-red text-sm font-semibold"><Star size={14} weight="fill" /> {rv.average.toFixed(1)}</div>
                  </div>
                  {rv.comment && <p className="text-sm text-slate-700 mt-1">{rv.comment}</p>}
                  <div className="text-[11px] text-slate-500 mt-2">Communication {rv.communication}/5 · Quality {rv.quality}/5 · Timeliness {rv.timeliness}/5 · Professionalism {rv.professionalism}/5</div>
                </div>
              ))}
            </div>
          </section>
        )}
      </article>
      <Footer />
    </div>
  );
}
